import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  listShares,
  createShare,
  revokeShare,
  shareUrl,
  type MeetingShare,
} from "@/lib/shares";

interface Props {
  meetingId: string;
}

export function ShareLinkPanel({ meetingId }: Props) {
  const { t } = useTranslation();
  const [shares, setShares] = useState<MeetingShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeTranscript, setIncludeTranscript] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listShares(meetingId)
      .then(setShares)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [meetingId]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const share = await createShare(meetingId, {
        includeTranscript,
        expiresInDays,
      });
      setShares((s) => [share, ...s]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (token: string) => {
    await navigator.clipboard.writeText(shareUrl(token));
    setCopied(token);
    setTimeout(() => setCopied((c) => (c === token ? null : c)), 2000);
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm(t("share.revokeConfirm"))) return;
    try {
      await revokeShare(id);
      setShares((s) => s.filter((x) => x.id !== id));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="bg-redi-surface rounded-xl p-4 mb-6">
      <h3 className="text-redi-text text-base font-semibold mb-3">
        {t("share.title")}
      </h3>

      <div className="flex flex-wrap gap-3 items-center mb-3">
        <label className="flex items-center gap-2 text-sm text-redi-muted">
          <input
            type="checkbox"
            checked={includeTranscript}
            onChange={(e) => setIncludeTranscript(e.target.checked)}
          />
          {t("share.includeTranscript")}
        </label>
        <select
          value={expiresInDays ?? "never"}
          onChange={(e) => {
            const v = e.target.value;
            setExpiresInDays(v === "never" ? null : Number(v));
          }}
          className="bg-redi-dark text-redi-text text-sm rounded-lg px-2 py-1"
        >
          <option value="never">{t("share.expiresNever")}</option>
          <option value="1">{t("share.expires1d")}</option>
          <option value="7">{t("share.expires7d")}</option>
          <option value="30">{t("share.expires30d")}</option>
        </select>
        <button
          onClick={handleCreate}
          disabled={creating}
          className="bg-redi-primary text-white text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-redi-primary/80 disabled:opacity-50"
        >
          {creating ? "…" : t("share.create")}
        </button>
      </div>

      {error && <p className="text-redi-error text-sm mb-2">{error}</p>}

      {loading ? (
        <div className="w-5 h-5 border-2 border-redi-primary border-t-transparent rounded-full animate-spin" />
      ) : shares.length === 0 ? (
        <p className="text-redi-muted text-sm">{t("share.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {shares.map((s) => (
            <li
              key={s.id}
              className="bg-redi-dark rounded-lg px-3 py-2 flex items-center gap-2"
            >
              <code className="flex-1 text-redi-text text-xs truncate">
                {shareUrl(s.token)}
              </code>
              <span className="text-redi-muted text-xs">
                {s.include_transcript ? "📝" : ""} {s.view_count} 👁
              </span>
              <button
                onClick={() => handleCopy(s.token)}
                className="text-redi-primary text-xs hover:underline"
              >
                {copied === s.token ? t("share.copied") : t("share.copy")}
              </button>
              <button
                onClick={() => handleRevoke(s.id)}
                className="text-redi-error text-xs hover:underline"
              >
                {t("share.revoke")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
